/**
 * SettingsTab — Campus settings with toggle cards.
 *
 * Four independently-savable settings groups:
 *   1. Architecture   (is_hybrid, show_new, show_legacy, is_cloud)
 *   2. Event Reporting (event_notes_active, event_notes_freeform, event_notes_preform, is_reason_editable, resident_trends)
 *   3. Output Features (app_active, text_messaging_active, email_active, pager_active, voice_active, udp_broadcast, offsite_active)
 *   4. Wellness        (careflow_v2, use_area_careflow, use_resident_careflow, use_device_careflow, use_event_careflow, is_resident_checkin, is_rounding)
 *
 * Each group tracks its own dirty state and shows Save/Cancel only when modified.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

// ---------------------------------------------------------------------------
// Info Tooltip — question mark icon with hover popover
// ---------------------------------------------------------------------------
const InfoTooltip = ({ title, content }) => {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState({});
  const ref = useRef(null);

  const handleEnter = () => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const popoverW = 320;
    const spaceRight = window.innerWidth - rect.left;
    const spaceBelow = window.innerHeight - rect.bottom;

    setStyle({
      position: 'fixed',
      zIndex: 9999,
      left: spaceRight < popoverW + 16 ? undefined : `${rect.left}px`,
      right: spaceRight < popoverW + 16 ? '16px' : undefined,
      top: spaceBelow < 120 ? undefined : `${rect.bottom + 6}px`,
      bottom: spaceBelow < 120 ? `${window.innerHeight - rect.top + 6}px` : undefined,
      width: `${popoverW}px`,
      background: 'var(--color-bg-surface, #fff)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--radius-lg, 8px)',
      boxShadow: 'var(--shadow-lg, 0 8px 24px rgba(0,0,0,0.12))',
      padding: 'var(--space-3, 12px)',
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      whiteSpace: 'normal',
    });
    setOpen(true);
  };

  return (
    <span
      ref={ref}
      onMouseEnter={handleEnter}
      onMouseLeave={() => setOpen(false)}
      style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 6, cursor: 'help', color: 'var(--color-text-muted)', position: 'relative' }}
      aria-label={title}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
      {open ? (
        <span style={style}>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 'var(--font-weight-semibold)' }}>{title}</span>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-body)', lineHeight: '1.5' }}>{content}</span>
        </span>
      ) : null}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Toggle Row — reusable row component
// ---------------------------------------------------------------------------
const ToggleRow = ({ label, description, checked, onChange, disabled, isChild, tooltip }) => (
  <div className={`cpp-toggle-row${isChild ? ' cpp-toggle-row--child' : ''}`}>
    <div className="cpp-toggle-info">
      <span className="cpp-toggle-label" style={{ display: 'inline-flex', alignItems: 'center' }}>
        {label}
        {tooltip ? <InfoTooltip title={tooltip.title} content={tooltip.content} /> : null}
      </span>
      {description && <span className="cpp-toggle-description">{description}</span>}
    </div>
    <input
      type="checkbox"
      className="cpp-toggle-switch"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      disabled={disabled}
    />
  </div>
);

// ---------------------------------------------------------------------------
// Settings Group Card — wraps a group with header + save/cancel footer
// ---------------------------------------------------------------------------
const SettingsGroupCard = ({ title, isDirty, onSave, onCancel, saving, editRights, children }) => (
  <div className="pp-section">
    <div className="pp-section__header">
      <h3 className="pp-section__title">{title}</h3>
      {editRights && isDirty && (
        <div className="pp-section__actions">
          <button className="dp-btn dp-btn--cancel dp-btn--sm" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button className="dp-btn dp-btn--primary dp-btn--sm" onClick={onSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      )}
    </div>
    <div className="pp-section__body cpp-toggle-group">
      {children}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Helper: compare two arrays of booleans
// ---------------------------------------------------------------------------
function arraysEqual(a, b) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// SETTINGS TAB
// ---------------------------------------------------------------------------
const SettingsTab = ({ campusData, editRights, onSave, saving, showToast }) => {
  // =========================================================================
  // 1. Architecture
  // =========================================================================
  const [isHybrid, setIsHybrid] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showLegacy, setShowLegacy] = useState(false);
  const [isCloud, setIsCloud] = useState(false);
  const [archOrigin, setArchOrigin] = useState([false, false, false, false]);

  // =========================================================================
  // 2. Event Reporting
  // =========================================================================
  const [eventNotesActive, setEventNotesActive] = useState(false);
  const [notesFreeform, setNotesFreeform] = useState(false);
  const [notesPreform, setNotesPreform] = useState(false);
  const [reasonEditable, setReasonEditable] = useState(false);
  const [residentTrends, setResidentTrends] = useState(false);
  const [reportOrigin, setReportOrigin] = useState([false, false, false, false, false]);

  // =========================================================================
  // 3. Output Features
  // =========================================================================
  const [appActive, setAppActive] = useState(false);
  const [textActive, setTextActive] = useState(false);
  const [emailActive, setEmailActive] = useState(false);
  const [pagerActive, setPagerActive] = useState(false);
  const [voiceActive, setVoiceActive] = useState(false);
  const [udpBroadcast, setUdpBroadcast] = useState(false);
  const [offsiteActive, setOffsiteActive] = useState(false);
  const [isGrandstream, setIsGrandstream] = useState(false);
  const [outputOrigin, setOutputOrigin] = useState([false, false, false, false, false, false, false, false]);

  // =========================================================================
  // 4. Wellness
  // =========================================================================
  const [careflowV2, setCareflowV2] = useState(false);
  const [areaCareflow, setAreaCareflow] = useState(false);
  const [resCareflow, setResCareflow] = useState(false);
  const [devCareflow, setDevCareflow] = useState(false);
  const [eventCareflow, setEventCareflow] = useState(false);
  const [checkin, setCheckin] = useState(false);
  const [rounds, setRounds] = useState(false);
  const [wellnessOrigin, setWellnessOrigin] = useState([false, false, false, false, false, false, false]);

  // =========================================================================
  // Sync from campusData whenever it changes
  // =========================================================================
  useEffect(() => {
    if (!campusData) return;

    const hy = !!campusData.is_hybrid;
    const nw = !!campusData.show_new;
    const lg = !!campusData.show_legacy;
    const cl = !!campusData.is_cloud;
    setIsHybrid(hy); setShowNew(nw); setShowLegacy(lg); setIsCloud(cl);
    setArchOrigin([hy, nw, lg, cl]);

    const en = !!campusData.event_notes_active;
    const nf = !!campusData.event_notes_freeform;
    const np = !!campusData.event_notes_preform;
    const re = !!campusData.is_reason_editable;
    const rt = !!campusData.resident_trends;
    setEventNotesActive(en); setNotesFreeform(nf); setNotesPreform(np); setReasonEditable(re); setResidentTrends(rt);
    setReportOrigin([en, nf, np, re, rt]);

    const mb = !!campusData.app_active;
    const tx = !!campusData.text_messaging_active;
    const em = !!campusData.email_active;
    const pg = !!campusData.pager_active;
    const vc = !!campusData.voice_active;
    const lc = !!campusData.udp_broadcast;
    const of = !!campusData.offsite_active;
    const gs = !!campusData.is_grandstream;
    setAppActive(mb); setTextActive(tx); setEmailActive(em); setPagerActive(pg);
    setVoiceActive(vc); setUdpBroadcast(lc); setOffsiteActive(of); setIsGrandstream(gs);
    setOutputOrigin([mb, tx, em, pg, vc, lc, of, gs]);

    const cf = !!campusData.careflow_v2;
    const ac = !!campusData.use_area_careflow;
    const rc = !!campusData.use_resident_careflow;
    const dc = !!campusData.use_device_careflow;
    const ec = !!campusData.use_event_careflow;
    const ck = !!campusData.is_resident_checkin;
    const rd = !!campusData.is_rounding;
    setCareflowV2(cf); setAreaCareflow(ac); setResCareflow(rc); setDevCareflow(dc);
    setEventCareflow(ec); setCheckin(ck); setRounds(rd);
    setWellnessOrigin([cf, ac, rc, dc, ec, ck, rd]);
  }, [campusData]);

  // =========================================================================
  // Dirty detection
  // =========================================================================
  const archDirty = !arraysEqual([isHybrid, showNew, showLegacy, isCloud], archOrigin);
  const reportDirty = !arraysEqual([eventNotesActive, notesFreeform, notesPreform, reasonEditable, residentTrends], reportOrigin);
  const outputDirty = !arraysEqual([appActive, textActive, emailActive, pagerActive, voiceActive, udpBroadcast, offsiteActive, isGrandstream], outputOrigin);
  const wellnessDirty = !arraysEqual([careflowV2, areaCareflow, resCareflow, devCareflow, eventCareflow, checkin, rounds], wellnessOrigin);

  // =========================================================================
  // Cancel handlers — reset to origin
  // =========================================================================
  const cancelArch = useCallback(() => {
    setIsHybrid(archOrigin[0]); setShowNew(archOrigin[1]); setShowLegacy(archOrigin[2]); setIsCloud(archOrigin[3]);
  }, [archOrigin]);

  const cancelReport = useCallback(() => {
    setEventNotesActive(reportOrigin[0]); setNotesFreeform(reportOrigin[1]); setNotesPreform(reportOrigin[2]); setReasonEditable(reportOrigin[3]); setResidentTrends(reportOrigin[4]);
  }, [reportOrigin]);

  const cancelOutput = useCallback(() => {
    setAppActive(outputOrigin[0]); setTextActive(outputOrigin[1]); setEmailActive(outputOrigin[2]); setPagerActive(outputOrigin[3]);
    setVoiceActive(outputOrigin[4]); setUdpBroadcast(outputOrigin[5]); setOffsiteActive(outputOrigin[6]); setIsGrandstream(outputOrigin[7]);
  }, [outputOrigin]);

  const cancelWellness = useCallback(() => {
    setCareflowV2(wellnessOrigin[0]); setAreaCareflow(wellnessOrigin[1]); setResCareflow(wellnessOrigin[2]); setDevCareflow(wellnessOrigin[3]);
    setEventCareflow(wellnessOrigin[4]); setCheckin(wellnessOrigin[5]); setRounds(wellnessOrigin[6]);
  }, [wellnessOrigin]);

  // =========================================================================
  // Save handlers — build payload/oldPayload and delegate to orchestrator
  // =========================================================================
  const campusId = campusData?.campus_id;

  const saveArch = useCallback(() => {
    onSave('architecture', {
      payload: [{ campus_id: campusId, is_hybrid: isHybrid ? 1 : 0, show_new: showNew ? 1 : 0, show_legacy: showLegacy ? 1 : 0, is_cloud: isCloud ? 1 : 0 }],
      oldPayload: [{ campus_id: campusId, is_hybrid: archOrigin[0] ? 1 : 0, show_new: archOrigin[1] ? 1 : 0, show_legacy: archOrigin[2] ? 1 : 0, is_cloud: archOrigin[3] ? 1 : 0 }],
      sessionKeys: ['is_hybrid', 'show_legacy', 'show_new', 'is_cloud'],
      sessionVals: [isHybrid, showLegacy, showNew, isCloud],
    });
  }, [onSave, campusId, isHybrid, showNew, showLegacy, isCloud, archOrigin]);

  const saveReport = useCallback(() => {
    onSave('reporting', {
      payload: [{ campus_id: campusId, event_notes_active: eventNotesActive ? 1 : 0, event_notes_freeform: notesFreeform ? 1 : 0, event_notes_preform: notesPreform ? 1 : 0, is_reason_editable: reasonEditable ? 1 : 0, resident_trends: residentTrends ? 1 : 0 }],
      oldPayload: [{ campus_id: campusId, event_notes_active: reportOrigin[0] ? 1 : 0, event_notes_freeform: reportOrigin[1] ? 1 : 0, event_notes_preform: reportOrigin[2] ? 1 : 0, is_reason_editable: reportOrigin[3] ? 1 : 0, resident_trends: reportOrigin[4] ? 1 : 0 }],
      sessionKeys: ['event_notes_active', 'event_notes_freeform', 'event_notes_preform', 'is_reason_editable', 'resident_trends'],
      sessionVals: [eventNotesActive, notesFreeform, notesPreform, reasonEditable, residentTrends],
    });
  }, [onSave, campusId, eventNotesActive, notesFreeform, notesPreform, reasonEditable, residentTrends, reportOrigin]);

  const saveOutput = useCallback(() => {
    const udpChanged = outputOrigin[5] !== udpBroadcast;
    onSave('output', {
      payload: [{ campus_id: campusId, app_active: appActive ? 1 : 0, text_messaging_active: textActive ? 1 : 0, email_active: emailActive ? 1 : 0, pager_active: pagerActive ? 1 : 0, voice_active: voiceActive ? 1 : 0, udp_broadcast: udpBroadcast ? 1 : 0, offsite_active: offsiteActive ? 1 : 0, is_grandstream: isGrandstream ? 1 : 0 }],
      oldPayload: [{ campus_id: campusId, app_active: outputOrigin[0] ? 1 : 0, text_messaging_active: outputOrigin[1] ? 1 : 0, email_active: outputOrigin[2] ? 1 : 0, pager_active: outputOrigin[3] ? 1 : 0, voice_active: outputOrigin[4] ? 1 : 0, udp_broadcast: outputOrigin[5] ? 1 : 0, offsite_active: outputOrigin[6] ? 1 : 0, is_grandstream: outputOrigin[7] ? 1 : 0 }],
      sessionKeys: ['app_active', 'text_messaging_active', 'email_active', 'pager_active', 'voice_active', 'udp_broadcast', 'offsite_active', 'is_grandstream'],
      sessionVals: [appActive, textActive, emailActive, pagerActive, voiceActive, udpBroadcast, offsiteActive, isGrandstream],
      reload: udpChanged,
    });
  }, [onSave, campusId, appActive, textActive, emailActive, pagerActive, voiceActive, udpBroadcast, offsiteActive, isGrandstream, outputOrigin]);

  const saveWellness = useCallback(() => {
    const cfChanged = wellnessOrigin[0] !== careflowV2;
    onSave('careflow', {
      payload: [{ campus_id: campusId, careflow_v2: careflowV2 ? 1 : 0, use_area_careflow: areaCareflow ? 1 : 0, use_resident_careflow: resCareflow ? 1 : 0, use_device_careflow: devCareflow ? 1 : 0, use_event_careflow: eventCareflow ? 1 : 0, is_resident_checkin: checkin ? 1 : 0, is_rounding: rounds ? 1 : 0 }],
      oldPayload: [{ campus_id: campusId, careflow_v2: wellnessOrigin[0] ? 1 : 0, use_area_careflow: wellnessOrigin[1] ? 1 : 0, use_resident_careflow: wellnessOrigin[2] ? 1 : 0, use_device_careflow: wellnessOrigin[3] ? 1 : 0, use_event_careflow: wellnessOrigin[4] ? 1 : 0, is_resident_checkin: wellnessOrigin[5] ? 1 : 0, is_rounding: wellnessOrigin[6] ? 1 : 0 }],
      sessionKeys: ['careflow_v2', 'use_area_careflow', 'use_resident_careflow', 'use_device_careflow', 'use_event_careflow', 'is_resident_checkin', 'is_rounding'],
      sessionVals: [careflowV2, areaCareflow, resCareflow, devCareflow, eventCareflow, checkin, rounds],
      reload: cfChanged,
    });
  }, [onSave, campusId, careflowV2, areaCareflow, resCareflow, devCareflow, eventCareflow, checkin, rounds, wellnessOrigin]);

  // =========================================================================
  // RENDER
  // =========================================================================
  return (
    <>
      {/* Architecture */}
      <SettingsGroupCard title="Architecture" isDirty={archDirty} onSave={saveArch} onCancel={cancelArch} saving={saving} editRights={editRights}>
        <ToggleRow
          label="Hybrid Setup"
          description="Setup contains a tap with both 2.0 and 4.0 servers connected"
          checked={isHybrid}
          onChange={(val) => {
            setIsHybrid(val);
            if (!val) { setShowNew(false); setShowLegacy(false); }
          }}
          disabled={!editRights}
        />
        <ToggleRow
          label="Show 4.0 Alerts"
          description="Events from 4.0 server appear in Pal Portal"
          checked={showNew}
          onChange={setShowNew}
          disabled={!editRights || !isHybrid}
          isChild
        />
        <ToggleRow
          label="Show 2.0 Alerts"
          description="Events from 2.0 server appear in Pal Portal"
          checked={showLegacy}
          onChange={setShowLegacy}
          disabled={!editRights || !isHybrid}
          isChild
        />
        <ToggleRow
          label="Upgraded to 5.0"
          description="Uses 5.0 Cloud Platform"
          checked={isCloud}
          onChange={setIsCloud}
          disabled={!editRights}
        />
      </SettingsGroupCard>

      {/* Event Reporting */}
      <SettingsGroupCard title="Event Reporting" isDirty={reportDirty} onSave={saveReport} onCancel={cancelReport} saving={saving} editRights={editRights}>
        <ToggleRow
          label="Event Notes"
          description="Can add notes to report events"
          checked={eventNotesActive}
          onChange={(val) => {
            setEventNotesActive(val);
            if (!val) { setNotesFreeform(false); setNotesPreform(false); }
          }}
          disabled={!editRights}
        />
        <ToggleRow
          label="Allow Freeform Notes"
          description="Allows notes to be added freeform (default if no option selected)"
          checked={notesFreeform}
          onChange={setNotesFreeform}
          disabled={!editRights || !eventNotesActive}
          isChild
        />
        <ToggleRow
          label="Use Pre-defined Note Options"
          description="Notes are added from predefined options"
          checked={notesPreform}
          onChange={setNotesPreform}
          disabled={!editRights || !eventNotesActive}
          isChild
        />
        <ToggleRow
          label="Reason Codes Editable"
          description="Can edit reason codes on report events"
          checked={reasonEditable}
          onChange={setReasonEditable}
          disabled={!editRights}
        />
        <ToggleRow
          label="Resident Analysis Trends"
          description="Show resident analysis trends on dashboards and resident profiles"
          checked={residentTrends}
          onChange={setResidentTrends}
          disabled={!editRights}
          tooltip={{
            title: 'About Resident Analysis Trends',
            content: (
              <>
                Surfaces actionable resident insights by detecting notable changes in call patterns, each with a severity level and weekly sparkline. Six triggers are monitored:
                <ul style={{ margin: '6px 0 0', paddingLeft: 18, lineHeight: '1.5' }}>
                  <li><strong>Volume Spike</strong> — significant increase in call volume this week vs. baseline.</li>
                  <li><strong>Sustained Increase</strong> — call volume rising for 3+ consecutive weeks.</li>
                  <li><strong>New High-Frequency</strong> — previously low-volume residents now among highest callers.</li>
                  <li><strong>Nighttime Increase</strong> — notable increase in calls between 10pm–6am.</li>
                  <li><strong>Reason Shift</strong> — significant change in call reason distribution.</li>
                  <li><strong>New Reason</strong> — calling for reasons not in recent history.</li>
                </ul>
              </>
            ),
          }}
        />
      </SettingsGroupCard>

      {/* Output Features */}
      <SettingsGroupCard title="Output Features" isDirty={outputDirty} onSave={saveOutput} onCancel={cancelOutput} saving={saving} editRights={editRights}>
        <ToggleRow
          label="Mobile App"
          description="System using mobile app"
          checked={appActive}
          onChange={setAppActive}
          disabled={!editRights}
        />
        <ToggleRow
          label="Text Messaging"
          description="System using text messaging"
          checked={textActive}
          onChange={setTextActive}
          disabled={!editRights}
        />
        <ToggleRow
          label="Email"
          description="System using email messaging"
          checked={emailActive}
          onChange={setEmailActive}
          disabled={!editRights}
        />
        <ToggleRow
          label="Pagers"
          description="System using paging transmitter"
          checked={pagerActive}
          onChange={setPagerActive}
          disabled={!editRights}
        />
        <ToggleRow
          label="Voice Calls"
          description="System using voice escalation"
          checked={voiceActive}
          onChange={setVoiceActive}
          disabled={!editRights}
        />
        <ToggleRow
          label="Light Controller"
          description="System outputs to light controllers"
          checked={udpBroadcast}
          onChange={setUdpBroadcast}
          disabled={!editRights}
        />
        <ToggleRow
          label="Offsite Monitoring"
          description="System outputs to integrated offsite monitoring provider (Careflow V2 only)"
          checked={offsiteActive}
          onChange={setOffsiteActive}
          disabled={!editRights}
        />
        <ToggleRow
          label="Grandstream SIP"
          description="Enables SIP / Grandstream speaker devices and mobile calling"
          checked={isGrandstream}
          onChange={setIsGrandstream}
          disabled={!editRights}
        />
      </SettingsGroupCard>

      {/* Wellness Settings */}
      <SettingsGroupCard title="Wellness Settings" isDirty={wellnessDirty} onSave={saveWellness} onCancel={cancelWellness} saving={saving} editRights={editRights}>
        <ToggleRow
          label="Using Careflow V2"
          description="Using new version 2 of careflow (5.0 only)"
          checked={careflowV2}
          onChange={setCareflowV2}
          disabled={!editRights}
        />
        <ToggleRow
          label="Using Area Careflows"
          description="Using area group based careflows"
          checked={areaCareflow}
          onChange={setAreaCareflow}
          disabled={!editRights}
        />
        <ToggleRow
          label="Using Resident Careflows"
          description="Using resident group based careflows"
          checked={resCareflow}
          onChange={setResCareflow}
          disabled={!editRights}
        />
        <ToggleRow
          label="Using Device Careflows"
          description="Using device specific careflows"
          checked={devCareflow}
          onChange={setDevCareflow}
          disabled={!editRights}
        />
        <ToggleRow
          label="Using Event Careflows"
          description="Using event category based careflows"
          checked={eventCareflow}
          onChange={setEventCareflow}
          disabled={!editRights}
        />
        <ToggleRow
          label="Using Resident Check-in"
          description="Enables resident check-in within wellness settings"
          checked={checkin}
          onChange={setCheckin}
          disabled={!editRights}
        />
        <ToggleRow
          label="Using Rounds"
          description="Enables rounds within wellness settings"
          checked={rounds}
          onChange={setRounds}
          disabled={!editRights}
        />
      </SettingsGroupCard>
    </>
  );
};

export default SettingsTab;
